export function productSourceId(product) {
  return Number(product?.produto_id ?? product?.id ?? 0) || null;
}

export function normalizeOperationalBoolean(value, fallback = false) {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value !== 0;
  }

  const normalized = String(value).trim().toLocaleLowerCase("pt-BR");
  if (
    ["0", "false", "nao", "não", "no", "off", "inativo", "pausado"].includes(
      normalized
    )
  ) {
    return false;
  }
  if (
    [
      "1",
      "true",
      "sim",
      "yes",
      "on",
      "ativo",
      "disponivel",
      "disponível",
    ].includes(normalized)
  ) {
    return true;
  }

  return fallback;
}

export function formatWeightedQuantity(value) {
  return value === null || value === undefined
    ? ""
    : String(value).replace(".", ",");
}

export function limitWeightedQuantityInput(value, previous = "") {
  const input = formatWeightedQuantity(value);
  return input.length <= 6 && /^(?:\d{0,2}|\d{1,2},\d{0,3})$/.test(input)
    ? input
    : formatWeightedQuantity(previous);
}

export function parseWeightedQuantity(value) {
  return Number(formatWeightedQuantity(value).replace(",", "."));
}

export function isWeightedQuantityValid(value) {
  const input = formatWeightedQuantity(value);
  return (
    input.length <= 6 &&
    /^\d{1,2}(?:,\d{1,3})?$/.test(input) &&
    parseWeightedQuantity(input) > 0
  );
}

export function normalizeOperationalProduct(product) {
  const sourceId = productSourceId(product);
  const groups = product?.grupos_opcoes || product?.opcoes || [];
  const pizza = product?.pizza || null;
  const priceCents = Number(
    product?.preco_base_centavos ??
      product?.preco_centavos ??
      Math.round(Number(product?.preco || 0) * 100)
  );

  return {
    ...product,
    id: sourceId,
    produto_id: sourceId,
    pesado: normalizeOperationalBoolean(product?.pesado),
    composto: normalizeOperationalBoolean(product?.composto),
    cardapio_produto_id: product?.cardapio_produto_id || null,
    descricao: product?.nome || product?.descricao || "Produto",
    preco: priceCents / 100,
    preco_centavos: priceCents,
    imagem: product?.imagem_url || product?.imagem || null,
    grupo: product?.grupo || null,
    grupo_id: product?.grupo_id ?? product?.id_grupo ?? product?.grupo?.id ?? 0,
    grupo_nome:
      product?.grupo_nome ||
      product?.grupo?.nome ||
      product?.categoria_nome ||
      "Outros",
    pizza,
    grupos_opcoes: Array.isArray(groups) ? groups : [],
    configuravel:
      normalizeOperationalBoolean(product?.configuravel) ||
      normalizeOperationalBoolean(product?.possui_opcoes) ||
      normalizeOperationalBoolean(product?.requer_configuracao) ||
      Boolean(product?.combo) ||
      Boolean(Array.isArray(product?.combos) && product.combos.length) ||
      Boolean(pizza) ||
      Boolean(Array.isArray(groups) && groups.length),
    restaurante_destinos: Array.isArray(
      product?.restaurante_destinos || product?.destinos
    )
      ? product.restaurante_destinos || product.destinos
      : [],
  };
}

export function normalizeOperationalCatalog(payload) {
  const value = payload?.data ?? payload ?? [];
  const grouped = Array.isArray(value)
    ? value
    : value?.grupos || value?.categorias || null;

  if (
    Array.isArray(grouped) &&
    grouped.some((group) => Array.isArray(group?.produtos || group?.items))
  ) {
    return grouped.map((group) => ({
      id: Number(group.id ?? group.grupo_id ?? 0),
      nome: group.nome || group.name || "Outros",
      produtos: (group.produtos || group.items || []).map(
        normalizeOperationalProduct
      ),
    }));
  }

  const products = Array.isArray(value)
    ? value
    : value?.produtos || value?.items || value?.data || [];
  const groupsById = new Map();

  (Array.isArray(products) ? products : []).forEach((rawProduct) => {
    const product = normalizeOperationalProduct(rawProduct);
    const groupId = Number(product.grupo_id || 0);

    if (!groupsById.has(groupId)) {
      groupsById.set(groupId, {
        id: groupId,
        nome: product.grupo_nome || "Outros",
        produtos: [],
      });
    }

    groupsById.get(groupId).produtos.push(product);
  });

  return Array.from(groupsById.values()).sort((left, right) =>
    left.nome.localeCompare(right.nome, "pt-BR")
  );
}

export function hasStructuredConfiguration(payload) {
  return Boolean(
    payload?.pizza?.tamanho ||
      payload?.pizza?.borda ||
      (Array.isArray(payload?.pizza?.sabores) &&
        payload.pizza.sabores.length) ||
      payload?.combo_id ||
      (Array.isArray(payload?.combo_itens) && payload.combo_itens.length) ||
      (Array.isArray(payload?.combo_composicao?.itens) &&
        payload.combo_composicao.itens.length) ||
      (Array.isArray(payload?.adicionais) && payload.adicionais.length)
  );
}

// Retorna a composição quando o cadastro já determina todas as escolhas.
// null indica que o operador ainda precisa escolher uma opção obrigatória.
export function buildQuickComboConfiguration(product) {
  const combo = product?.combo || product?.combos?.[0];
  if (!combo) return null;

  const configuration = fixedProductConfiguration(product, combo);
  if (!configuration) return null;

  const items = [];
  for (const item of combo.itens || []) {
    if (!item.produto) return null;
    const itemConfiguration = fixedProductConfiguration(item.produto, item);
    if (!itemConfiguration) return null;
    items.push({
      chave: item.chave,
      produto: item.produto_slug,
      ...itemConfiguration,
    });
  }
  return { ...configuration, combo_id: combo.id, combo_itens: items };
}

function operationalOptionAvailable(option) {
  return ["ativo", "active", "disponivel", "available"].every((flag) =>
    normalizeOperationalBoolean(option?.[flag], true)
  );
}

function operationalOptionKey(option) {
  return String(option.id ?? option.codigo ?? option.nome);
}

function fixedProductConfiguration(product, definition) {
  const groups = product.grupos_opcoes || product.opcoes || [];
  if (
    groups.some(
      (group) =>
        operationalOptionAvailable(group) &&
        (normalizeOperationalBoolean(group.obrigatorio) ||
          normalizeOperationalBoolean(group.required) ||
          group.tipo === "acompanhamento_obrigatorio" ||
          Number(group.minimo ?? group.min ?? 0) > 0)
    )
  )
    return null;

  const configuration = { adicionais: [] };
  if (!product.pizza) return configuration;

  const sizes = (product.pizza.tamanhos || product.pizza.sizes || []).filter(
    operationalOptionAvailable
  );
  const size = definition.pizza_tamanho_slug
    ? sizes.find(
        (item) => operationalOptionKey(item) === definition.pizza_tamanho_slug
      )
    : sizes.length === 1
    ? sizes[0]
    : null;
  if (!size) return null;

  const sizeKey = operationalOptionKey(size);
  const flavors = (product.pizza.sabores || product.pizza.flavors || []).filter(
    (flavor) => {
      const allowedSizes =
        flavor.tamanhos ||
        flavor.tamanho_ids ||
        flavor.available_size_ids ||
        [];
      const allowedFlavors = definition.pizza_sabores_slugs || [];
      return (
        operationalOptionAvailable(flavor) &&
        (!allowedSizes.length ||
          allowedSizes.some(
            (allowedSize) =>
              String(allowedSize?.id ?? allowedSize?.codigo ?? allowedSize) ===
              sizeKey
          )) &&
        (!allowedFlavors.length ||
          allowedFlavors.includes(operationalOptionKey(flavor)))
      );
    }
  );
  if (flavors.length !== 1) return null;

  configuration.pizza = {
    tamanho: sizeKey,
    sabores: [operationalOptionKey(flavors[0])],
    borda: null,
  };
  return configuration;
}
